import { StyledSelect } from "@ando/ui";
import {
  Beaker,
  Bell,
  Brain,
  CheckSquare,
  Database,
  FileSearch,
  History,
  Layers,
  Workflow,
} from "lucide-react";
import * as React from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { getSession, isPlatformAdmin } from "@ando/core/auth";
import {
  SurfaceHeader,
  SurfaceLayout,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from "@ando/ui";

import { useHypermemTenants } from "../hooks/useAdminHypermem";

import { AdminPageHint } from "./AdminPageHint";
import { HmApprovalQueueTab } from "./hypermem/HmApprovalQueueTab";
import { HmAuditTrailTab } from "./hypermem/HmAuditTrailTab";
import { HmEvolutionLogTab } from "./hypermem/HmEvolutionLogTab";
import { HmExperimentsTab } from "./hypermem/HmExperimentsTab";
import { HmFactsBrowserTab } from "./hypermem/HmFactsBrowserTab";
import { HmNudgesTab } from "./hypermem/HmNudgesTab";
import { HmOverviewTab } from "./hypermem/HmOverviewTab";
import { HmReasoningTracesTab } from "./hypermem/HmReasoningTracesTab";
import { HmTenantsTab } from "./hypermem/HmTenantsTab";
import { HmWorkflowSuggestionsTab } from "./hypermem/HmWorkflowSuggestionsTab";

/**
 * HyperMem admin shell (v2 port of v1 ``HypermemAdminPage``). Cognitive-memory
 * governance: 9 sub-tabs over the episodic/semantic memory subsystem. A global
 * tenant selector scopes the tabs; "Tutti i tenant" = platform-wide aggregate
 * (the tabs that require a concrete tenant render a hint until one is chosen).
 *
 * Each tab is lazy: TanStack Query only fires when its tab is mounted.
 */

/**
 * The sub-tab ids, as a tuple so the hypermem route's ``validateSearch`` can
 * whitelist ``?tab=`` against the SAME source of truth (mirror of how
 * ``ORG_DETAIL_TAB_IDS`` drives the org detail route).
 */
export const HM_TAB_IDS = [
  "overview",
  "approval",
  "workflow",
  "experiments",
  "evolution",
  "facts",
  "audit",
  "traces",
  "nudges",
  "tenants",
] as const;

export type HmTabId = (typeof HM_TAB_IDS)[number];

const TABS: {
  id: HmTabId;
  label: MessageDescriptor;
  icon: typeof Brain;
  requiresTenant?: boolean;
  /**
   * Quando ``true`` la tab è visibile SOLO per super-admin platform-wide
   * (mirror del BE ``require_true_platform_admin``). Le tab tenant-scoped
   * stanno OK con un org-admin: questa flag serve per le surface che
   * espongono dati cross-tenant (es. comparative view per tenant) dove
   * un org-admin non deve vedere i count degli altri tenant.
   */
  requiresSuperadmin?: boolean;
}[] = [
  { id: "overview", label: msg`Panoramica`, icon: Brain },
  { id: "approval", label: msg`Approval Queue`, icon: CheckSquare },
  { id: "workflow", label: msg`Workflow Suggestions`, icon: Workflow, requiresTenant: true },
  { id: "experiments", label: msg`Esperimenti A/B`, icon: Beaker, requiresTenant: true },
  { id: "evolution", label: msg`Evolution Log`, icon: History },
  { id: "facts", label: msg`Facts Browser`, icon: Database, requiresTenant: true },
  { id: "audit", label: msg`Audit Trail`, icon: FileSearch, requiresTenant: true },
  { id: "traces", label: msg`Reasoning Traces`, icon: Brain, requiresTenant: true },
  { id: "nudges", label: msg`Nudges`, icon: Bell, requiresTenant: true },
  { id: "tenants", label: msg`Per Tenant`, icon: Layers, requiresSuperadmin: true },
];

function TenantNeeded(): React.ReactElement {
  return (
    <div className="rounded-xl border border-hairline bg-cream/40 p-6 text-center text-ink-3 text-sm">
      <Trans>Seleziona un tenant in alto per visualizzare questa sezione.</Trans>
    </div>
  );
}

export function AdminHypermem(): React.ReactElement {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  // Active sub-tab is URL-addressable (``?tab=``) — deep-links from audit
  // rows / Linear tickets land on the right tab, back/forward + refresh keep
  // the operator where they were. The route ``validateSearch`` coerces any
  // unknown value back to "overview".
  const { tab: activeTab } = useSearch({ from: "/_authed/_modal/admin/hypermem" });
  const setActiveTab = React.useCallback(
    (id: HmTabId) => {
      void navigate({
        to: "/admin/hypermem",
        search: (prev) => ({ ...prev, tab: id }),
      });
    },
    [navigate],
  );
  const [tenantId, setTenantId] = React.useState<string | null>(null);

  // Gate cross-tenant tabs (es. "Per Tenant") al solo super-admin —
  // mirror del BE ``require_true_platform_admin``. Un org-admin che
  // vedesse la tab e cliccasse riceverebbe 403 + lista vuota silenziosa
  // (Jun 28, 2026 incident): nasconderla previene confusione UX.
  const visibleTabs = React.useMemo(() => {
    const canCrossTenant = isPlatformAdmin(getSession(), "cross-tenant");
    return TABS.filter((tab) => !tab.requiresSuperadmin || canCrossTenant);
  }, []);

  // Tenant selector options — reuse the comparative endpoint (org names).
  const tenants = useHypermemTenants({ limit: 200 });
  const tenantOptions = tenants.data?.tenants ?? [];

  // Auto-pick the first tenant the FIRST time tenants load — otherwise
  // 6 of the 10 tabs (workflow / experiments / facts / audit / traces /
  // nudges) render the "Select a tenant above" empty state and the
  // admin has to click the dropdown before anything useful appears.
  // After the user explicitly chooses "Tutti (platform-wide)" (value="")
  // we never auto-pick again — tracked via ``hasAutoPicked`` ref.
  const hasAutoPicked = React.useRef(false);
  React.useEffect(() => {
    if (hasAutoPicked.current || tenantId !== null) return;
    const first = tenantOptions[0];
    if (!first) return;
    hasAutoPicked.current = true;
    setTenantId(first.tenant_id);
  }, [tenantId, tenantOptions]);

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          className="justify-start gap-x-5 px-6 pt-3 pb-4"
          title="HyperMem"
          meta={
            <>
              <div className="font-medium text-ink-2 text-sm tabular-nums">
                <span>
                  <Trans>
                    Memoria cognitiva · fatti episodici/semantici · esperimenti · governance
                    proattiva
                  </Trans>
                </span>
              </div>
              <AdminPageHint>
                <Trans>
                  Governance del sottosistema di memoria cognitiva: consolidamento, esperimenti A/B
                  sui prompt, suggerimenti di automazione e nudge proattivi — con audit completo e
                  isolamento per tenant.
                </Trans>
              </AdminPageHint>
            </>
          }
          actions={
            <label className="flex items-center gap-2 text-[12.5px] text-ink-2">
              <span className="text-ink-3">
                <Trans>Tenant</Trans>
              </span>
              <StyledSelect
                value={tenantId ?? ""}
                onChange={(e) => setTenantId(e.target.value || null)}
                className="rounded-lg border border-hairline bg-card/55 px-2.5 py-1 text-ink-1"
              >
                <option value="">{t`Tutti (platform-wide)`}</option>
                {tenantOptions.map((t) => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.organization_name ?? t.tenant_id}
                  </option>
                ))}
              </StyledSelect>
            </label>
          }
        />
      }
      contentAriaLabel={t`HyperMem — governance memoria cognitiva`}
      contentClassName="px-6"
    >
      <div className="">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as HmTabId)}
          className="space-y-4"
        >
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 overflow-x-auto">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.id} value={tab.id} className={cn("gap-1.5 text-[12.5px]")}>
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {i18n._(tab.label)}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="overview">
            {activeTab === "overview" && <HmOverviewTab tenantId={tenantId} />}
          </TabsContent>
          <TabsContent value="approval">
            {activeTab === "approval" && <HmApprovalQueueTab tenantId={tenantId} />}
          </TabsContent>
          <TabsContent value="workflow">
            {activeTab === "workflow" &&
              (tenantId ? <HmWorkflowSuggestionsTab tenantId={tenantId} /> : <TenantNeeded />)}
          </TabsContent>
          <TabsContent value="experiments">
            {activeTab === "experiments" &&
              (tenantId ? <HmExperimentsTab tenantId={tenantId} /> : <TenantNeeded />)}
          </TabsContent>
          <TabsContent value="evolution">
            {activeTab === "evolution" && <HmEvolutionLogTab tenantId={tenantId} />}
          </TabsContent>
          <TabsContent value="facts">
            {activeTab === "facts" &&
              (tenantId ? <HmFactsBrowserTab tenantId={tenantId} /> : <TenantNeeded />)}
          </TabsContent>
          <TabsContent value="audit">
            {activeTab === "audit" &&
              (tenantId ? <HmAuditTrailTab tenantId={tenantId} /> : <TenantNeeded />)}
          </TabsContent>
          <TabsContent value="traces">
            {activeTab === "traces" &&
              (tenantId ? <HmReasoningTracesTab tenantId={tenantId} /> : <TenantNeeded />)}
          </TabsContent>
          <TabsContent value="nudges">
            {activeTab === "nudges" &&
              (tenantId ? <HmNudgesTab tenantId={tenantId} /> : <TenantNeeded />)}
          </TabsContent>
          <TabsContent value="tenants">
            {activeTab === "tenants" && <HmTenantsTab onSelectTenant={(id) => setTenantId(id)} />}
          </TabsContent>
        </Tabs>
      </div>
    </SurfaceLayout>
  );
}
